import { Component, inject, Input, OnInit, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MY_DATE_FORMATS, MY_DATE_ADAPTER } from '../../../license/license-date-format';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SharedService } from '@shared/services/shared.service';
import { IAffectedPerson } from 'app/models/compliance/affected-person';

export interface IAffectedPersonDetails {
  firstName: string;
  middleName: string;
  lastName: string;
  address: string;
  phone: string;
  email: string;
}

@Component({
  selector: 'app-affected-person',
  standalone: false,
  templateUrl: './affected-person.component.html',
  styleUrl: './affected-person.component.scss',
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    MY_DATE_FORMATS,
    MY_DATE_ADAPTER
  ]
})
export class AffectedPersonComponent  implements OnInit{
  @ViewChild('firstNameInput') firstNameInput!: ElementRef<HTMLInputElement>;
  @Input() personDetails: IAffectedPersonDetails = {
    firstName: '',
    middleName: '',
    lastName: '',
    address: '',
    phone: '',
    email: ''
  };
  @Input() isClient: boolean = false;
  public affectedPeople: IAffectedPerson[] = [];
  public otherContactReason: string = '';
  public relationshipOptions: string[] = [
    'Current Staff',
    'Former Staff',
    'OR APS',
    'OR DHS',
    'Other State Agency',
    'Federal Agency',
    'Media',
    'Other'
  ]
  public affectedPersonForm = new FormGroup({
    firstName: new FormControl(''),
    middleName: new FormControl(''),
    lastName: new FormControl(''),
    address: new FormControl(''),
    phone: new FormControl(''),
    email: new FormControl('', Validators.email),
    dob: new FormControl<Date | null>(null),
    gender: new FormControl(''),
    relationship: new FormControl(''),
    relationshipOther: new FormControl(''),
    canContact: new FormControl(null),
    whyNoContact: new FormControl(''),
    otherContactReason: new FormControl(''),
    servicesReceived: new FormControl(''),
    clientSchedule: new FormControl(''),
    dateAdmitted: new FormControl<Date | null>(null),
    currentPatient: new FormControl(null),
    dateDischarged: new FormControl<Date | null>(null)
  });

  private sharedService = inject(SharedService);

  ngOnInit(): void {
    if (this.isClient){
      this.affectedPersonForm.patchValue(this.personDetails);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['isClient']) {
      if (this.isClient) {
        this.affectedPersonForm.patchValue(this.personDetails);
      }
      else{
        this.affectedPersonForm.reset();
      }
    }
  }

  addAffectedPerson(): void {
    let formValue = this.affectedPersonForm.getRawValue();
    let affectedPerson: IAffectedPerson = {
      firstName: formValue.firstName,
      middleName: formValue.middleName,
      lastName: formValue.lastName,
      address: formValue.address,
      phone: formValue.phone,
      email: formValue.email,
      dob: formValue.dob?.toDateString() ?? null,
      gender: formValue.gender,
      relationship: formValue.relationship,
      relationshipOther: formValue.relationshipOther,
      canContact: formValue.canContact,
      whyNoContact: formValue.whyNoContact,
      otherContactReason: formValue.otherContactReason,
      clientNarrative: '',
      incidentLocation: '',
      servicesReceived: formValue.servicesReceived,
      clientSchedule: formValue.clientSchedule,
      dateAdmitted: formValue.dateAdmitted?.toDateString() ?? null,
      currentPatient: formValue.currentPatient,
      dateDischarged: formValue.dateDischarged?.toDateString() ?? null
    };
    this.affectedPeople.push(affectedPerson);
    this.otherContactReason = '';
    this.affectedPersonForm.reset();
    this.focusFirstName();
  }

  private focusFirstName(): void {
    // Use a timeout to ensure DOM updates complete before focusing
    setTimeout(() => {
      if (this.firstNameInput && this.firstNameInput.nativeElement) {
        try {
          // Scroll the input into view so the user can see it, then focus
          try {
            this.firstNameInput.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch {
            // Ignore if scrollIntoView not supported
          }
          this.firstNameInput.nativeElement.focus();
        } catch { }
      }
    }, 0);
  }

  removeAffectedPerson(index: number): void {
    this.sharedService.openConfirm('Delete this patient/client?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.affectedPeople.splice(index, 1);
        }
      }
    );
  }

  relationShipExtraDetails(relationship: string): boolean {
    return this.relationshipOptions.includes(relationship);
  }

}
